import Reveal from '../Reveal';
import Section from '../Section';

/**
 * Client logo strip, directly beneath the hero.
 *
 * TYPOGRAPHIC WORDMARKS, NOT BRAND ASSETS. Recorded decision on epic #7918 and
 * restated on #7957: Apple's trademark guidelines prohibit logo use that
 * implies association or endorsement, and these are platform-delivery
 * relationships rather than client endorsements. Setting the names in Outfit
 * carries the same credibility with no asset sourcing and no legal exposure.
 * So: no <img>, no trademark symbols, no brand colours, no links to brand
 * sites. These are words.
 *
 * THIRTY-THREE NAMES, AND WHY A TICKER. #7918 shipped five, chosen to match the
 * five the hero paragraph names. #7957 supplied the full list, which does not
 * fit a static row: at 33 names a flex-wrap strip becomes four or five stacked
 * lines directly under the hero, which is a wall, and the section is meant to
 * be evidence in passing. A marquee shows a few at a time and lets the rest
 * arrive, so length becomes duration rather than height.
 *
 * The hero paragraph still names Apple, Red Bull, Disney, BBC and ESPN, and
 * those five open the list so the strip starts by reinforcing the copy above
 * it. Spotify appeared twice in the supplied list and is carried once.
 *
 * Curating back to a shorter set is a one-line edit to WORDMARKS; nothing else
 * in this component depends on the count.
 *
 * MOTION. Own <style> block rather than globals.css, matching
 * components/ConvergenceField.tsx: the shell owns the token layer and a
 * component owns its own motion, so the two never have to be edited together.
 * next.config.ts sets style-src 'self' 'unsafe-inline'.
 *
 * NO VIEWPORT-DEPENDENT TYPE OR GAP inside the track, deliberately. The loop is
 * a translate of -50% across a track holding two identical runs, so -50% is
 * exactly one run wide and the seam is invisible. That percentage is resolved
 * against the track's own box, and #7957 established that Safari does not
 * re-resolve a percentage transform inside running keyframes after a layout
 * change (see the .fp-dot note in ConvergenceField.tsx). Holding the type size
 * and the gap constant makes the track's max-content width independent of the
 * viewport, so no resize can change the box the percentage was resolved
 * against, and the loop cannot desync. Same principle as the fix in that file:
 * remove the dependency rather than work around the engine.
 */
const WORDMARKS = [
  'Apple',
  'Red Bull',
  'Disney',
  'BBC',
  'ESPN',
  'Google',
  'Netflix',
  'Nike',
  'Adidas',
  'Coca-Cola',
  'Pepsi',
  'Spotify',
  'Sony',
  'Vimeo',
  'Hulu',
  'NBC',
  'TBS',
  'Viacom',
  'Target',
  'Walmart',
  'Unilever',
  'P&G',
  "L'Oréal",
  'General Electric',
  'AT&T',
  'T-Mobile',
  'OnePlus',
  'Hilti',
  'Kaplan',
  'Ted Baker',
  'Gap Inc.',
  'Coach',
  'Blippar',
] as const;

const TICKER_CSS = `
/* The track is two identical runs. Each run carries a trailing pad equal to its
   internal gap, so both runs are exactly the same width and -50% lands on the
   start of the second one. Without that pad the seam loses a gap and the names
   collide once per cycle. */
.fp-ticker {
  overflow: hidden;
  -webkit-mask-image: linear-gradient(to right,
    rgba(0,0,0,0) 0%, rgba(0,0,0,1) 7%, rgba(0,0,0,1) 93%, rgba(0,0,0,0) 100%);
          mask-image: linear-gradient(to right,
    rgba(0,0,0,0) 0%, rgba(0,0,0,1) 7%, rgba(0,0,0,1) 93%, rgba(0,0,0,0) 100%);
}

/* 45s over a 2650px run is about 59px/s. Set by measuring the run rather than
   by taste: below roughly 30px/s a marquee reads as drift, as though the page
   were sliding, and above roughly 90px/s the names stop being legible in
   passing, which is the entire point of the section. 59 crosses the strip in
   about 20 seconds and lets an eye settle on a name without stopping it. */
.fp-ticker-track {
  display: flex;
  width: max-content;
  animation: fpTicker 45s linear infinite;
}

/* A reader who wants to actually read a name can stop it, which is the whole
   reason the names are here. focus-within covers the keyboard path even though
   nothing inside is focusable today. */
.fp-ticker:hover .fp-ticker-track,
.fp-ticker:focus-within .fp-ticker-track {
  animation-play-state: paused;
}

.fp-ticker-run {
  display: flex;
  flex: none;
  align-items: baseline;
  gap: 30px;
  padding: 0 30px 0 0;
  margin: 0;
  list-style: none;
}

.fp-ticker-run > li {
  flex: none;
  white-space: nowrap;
}

@keyframes fpTicker {
  from { transform: translate3d(0, 0, 0); }
  to   { transform: translate3d(-50%, 0, 0); }
}

/* Reduced motion: a static, readable list, not a frozen mid-scroll frame.
   The mask and the overflow clip both go, or the first and last names in the
   resting position would be faded or cut. The duplicate run is removed from
   the layout entirely rather than hidden, so the wrap is over 33 names and not
   66. */
@media (prefers-reduced-motion: reduce) {
  .fp-ticker {
    overflow: visible;
    -webkit-mask-image: none;
            mask-image: none;
  }

  .fp-ticker-track {
    display: block;
    width: auto;
    animation: none;
  }

  .fp-ticker-run {
    flex-wrap: wrap;
    row-gap: 14px;
    padding-right: 0;
  }

  .fp-ticker-dup {
    display: none;
  }
}
`;

/**
 * One pass of the list. The track holds two: the second exists only so the
 * loop has something to scroll into, so it is aria-hidden and a screen reader
 * reads the 33 names once, from the run the caption labels.
 *
 * role="list" is not redundant. Safari drops list semantics from a <ul> whose
 * list-style is none, which is exactly what the ticker sets, and the explicit
 * role puts them back.
 */
function Run({ duplicate = false }: { duplicate?: boolean }) {
  return (
    <ul
      role="list"
      className={duplicate ? 'fp-ticker-run fp-ticker-dup' : 'fp-ticker-run'}
      aria-hidden={duplicate || undefined}
      aria-labelledby={duplicate ? undefined : 'client-logo-strip-caption'}
    >
      {WORDMARKS.map((name) => (
        <li
          key={name}
          className="font-display text-[17px] font-medium tracking-[-0.02em] text-caption opacity-85"
        >
          {name}
        </li>
      ))}
    </ul>
  );
}

export default function ClientLogoStrip() {
  return (
    <Section spacing="tight" className="border-b border-line">
      {/* One reveal for the whole strip, not one per name. The names are a
          single statement, and revealing them in sequence would put a second
          motion on top of the one the ticker already carries. */}
      <Reveal>
        <style>{TICKER_CSS}</style>
        <p id="client-logo-strip-caption" className="font-sans text-xs uppercase tracking-[0.2em] text-caption">
          Platforms delivered for
        </p>
        <div className="fp-ticker mt-[14px]">
          <div className="fp-ticker-track">
            <Run />
            <Run duplicate />
          </div>
        </div>
      </Reveal>
    </Section>
  );
}
